import ControlFactory from '@/Factories/ControlFactory';
import RADApplication from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import CurrencyHelper from '@/Utility/CurrencyHelper';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';
import LocatorHelper from '@/Utility/LocatorHelper';
import CurrencyTextBoxViewModel from '@/ViewModels/Controls/CurrencyTextBoxViewModel';
import NumericTextBoxViewModel from '@/ViewModels/Controls/NumericTextBoxViewModel';
import DropDownListViewModel from '@/ViewModels/Controls/DropDownListViewModel';
import CurrencyTextBox from '@/Controls/CurrencyTextBox';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import { FilterInfo } from '#/DefaultViewExtensionInterfaces';

export default class CurrencyTextBoxFactory extends ControlFactory {

    private jQueryControl: JQuery;
    private CurrencyTextBoxViewModel = ComponentManager.getViewModel<typeof CurrencyTextBoxViewModel>('CurrencyTextBoxViewModel');
    private NumericTextBoxViewModel = ComponentManager.getViewModel<typeof NumericTextBoxViewModel>('NumericTextBoxViewModel');
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModel>('DropDownListViewModel');
    private CurrencyTextBox = ComponentManager.getControl<typeof CurrencyTextBox>('CurrencyTextBox');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    private technicalNameRootProperty: string;
    private technicalNameCurrencyID: string;
    private technicalNameChanged: string;
    private technicalNameListViewValue: string;
    private technicalNameValue: string;
    private technicalNameEURValue: string;

    /**
     * @override
     */
    public viewModel: CurrencyTextBoxViewModel;

    public constructor(params: any) {
        super(params);
        this.technicalNameRootProperty = '';
        this.technicalNameCurrencyID = '';
        this.technicalNameChanged = '';
        this.technicalNameListViewValue = '';
        this.technicalNameValue = '';
        this.technicalNameEURValue = '';
    }

    /**
     * @override
     */
    control: CurrencyTextBox;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = this.createViewModel();

        this.control = new this.CurrencyTextBox(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.technicalNameValue]);
        this.registerDefaultValidation([this.technicalNameRootProperty, this.technicalNameValue, this.technicalNameCurrencyID]);
        this.registerTypeValidation(this.technicalNameValue, (value: any, dotNetTypeName: any) => {
            if (dotNetTypeName != null) {
                let type = dotNetTypeName;
                if (type != null) {
                    let info = NumericTypeHelper.getDotNetTypeInfo(type);
                    if (info != null) {
                        let min = this.viewModel.numericTextBox.min() != null ? Math.max(this.viewModel.numericTextBox.min(), info.min) : info.min;
                        let max = this.viewModel.numericTextBox.max() != null ? Math.min(this.viewModel.numericTextBox.max(), info.max) : info.max;

                        if (value < min || value > max) {
                            return false;
                        }
                    }
                }
            }

            return true;
        });

        return this.jQueryControl;
    }

    private createViewModel(): CurrencyTextBoxViewModel {
        this.validateTechName();
        return new this.CurrencyTextBoxViewModel({
            factory: this,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            parentLocator: this.locator,
            // durch ein IEG wird der Embedded-Type aufgeloest, welcher hier zu resultiert, dass 'ABC.Value' als Locator erzeugt wird. '.Value' ist aber auch Teil des Technical-Name der NumericTextBox => gleicher Locator
            locator: this.detailViewItem.name.replace('.Value', ''), 
            numericTextBox: new this.NumericTextBoxViewModel({
                value: this.context.genericObject.getComputed(this.technicalNameValue, {
                    read: (observable: any) => {
                        let result = CurrencyHelper.toClientValue(observable());
                        return result;
                    },
                    write: (observable: any, value: any) => {
                        let result = CurrencyHelper.toServerValue(value);
                        observable(result);
                        this.context.genericObject.isModified(true);
                    }
                }),
                tooltip: new this.LabelViewModel({
                    ergName: this.detailViewItem.tooltip
                }),
                showSpinners: false,
                parentLocator: this.locator,
                locator: this.technicalNameValue,
                isEnabled: this.isEnabled,
                isFocusable: this.isFocusable,
                isValid: this.isValid,
                invalidationText: this.invalidationText,
                dotNetTypeName: this.detailViewItem.dotNetTypeName,
                isRequired: this.isRequired
            }),
            dropDownList: new this.DropDownListViewModel({
                data: this.defineComputed(() => {
                    let currencyId = this.context.genericObject.getObservable(this.technicalNameCurrencyID)();
                    return CurrencyHelper.getAllExchangeCurrenciesForCurrency(~~currencyId, ['shortname', 'sorting']);
                }),
                tooltip: new this.LabelViewModel({
                    ergName: this.detailViewItem.tooltip
                }),
                isEnabled: this.isEnabled,
                isFocusable: this.isFocusable,
                parentLocator: this.locator,
                locator: LocatorHelper.buildLocator(this.detailViewItem.name, 'Currency'),
                itemLocatorStrategy: (item: any) => {
                    return item.shortname;
                },
                value: this.context.genericObject.getObservable(this.technicalNameCurrencyID),
                dataTextField: 'shortname',
                dataValueField: 'isO4217NumberCode'
            })
        });
    }

    private getServerValue(): number | string {
        let value = this.viewModel?.valueInEuro();

        if (value == null || _.isNaN(value)) {
            return '';
        }

        let serverValue = CurrencyHelper.toServerValue(value);

        return serverValue || '';
    }

    public getValueForFilter(): Object {
        if (this.viewModel && !this.viewModel.isDisposed) {
            let ret = {};

            ret[this.technicalNameValue] = this.context.genericObject.getValue(this.technicalNameValue);
            ret[this.technicalNameCurrencyID] = +this.context.genericObject.getValue(this.technicalNameCurrencyID);

            return ret;
        }

        return null;
    }

    public getFilterInfo(): FilterInfo {
        return {
            field: this.technicalNameRootProperty.replace(/\.ListViewValue/, '.EURValue'),
            value: this.getServerValue().toString()
        }
    }
    
    private validateTechName(): void {
        this.technicalNameRootProperty = this.detailViewItem.name;

        if (this.detailViewItem.name.substring(this.detailViewItem.name.length - 6) === '.Value') {
            this.technicalNameRootProperty = this.detailViewItem.name.substring(0, this.detailViewItem.name.length - 6);
        }

        this.technicalNameCurrencyID = this.technicalNameRootProperty + '.CurrencyID';

        this.technicalNameChanged = this.technicalNameRootProperty + '.Changed';
        this.technicalNameListViewValue = this.technicalNameRootProperty + '.ListViewValue';
        this.technicalNameValue = this.technicalNameRootProperty + '.Value';
        this.technicalNameEURValue = this.technicalNameRootProperty + '.EURValue';
    }
}

ComponentManager.setFactory('CurrencyTextBox', CurrencyTextBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');